// Shared Google sign-in flow: popup auth + first-login bookkeeping.
// Single source of truth used by AuthPage and the guest exam signup modal,
// so every Google account gets its profile doc and signup tracking no matter
// where the button lives.

import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { initializeUserData, logUserActivity } from './activityLogger';
import { trackSignupStart, trackSignupComplete } from './conversionEvents';

/**
 * Runs the Google popup and, for first-time users, creates the profile doc
 * and fires signup tracking (signup_start/signup_complete + activity log).
 * Existing users just get a login activity entry.
 *
 * @returns {Promise<{ user: import('firebase/auth').User, isNewUser: boolean }>}
 * @throws Firebase auth errors unchanged (e.g. auth/popup-closed-by-user)
 *         callers map them to UI messages.
 */
export async function signInWithGoogleAndProcess() {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  const user = result.user;

  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  const isNewUser = !userSnap.exists();

  if (isNewUser) {
    // Fire signup_start here so it always precedes signup_complete for new Google users,
    // even if the user was on the login view or the popup obscured the earlier call.
    trackSignupStart({ method: 'google' });
    const nameParts = (user.displayName || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    await initializeUserData(user, { firstName, lastName });

    await logUserActivity({
      action: 'signup',
      details: {
        registrationMethod: 'google',
        timestamp: new Date(),
      },
    });

    trackSignupComplete({ method: 'google', userId: user.uid });
  } else {
    await logUserActivity({
      action: 'login',
      details: {
        loginMethod: 'google',
        timestamp: new Date(),
      },
    });
  }

  return { user, isNewUser };
}
